import { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Search } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import Header from "@/layouts/Header/Header"
import customerAuthService from "@/service/auth.service/customer.auth.service"

export default function FAQsPage() {
  const [faqs, setFaqs] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [filteredFaqs, setFilteredFaqs] = useState([])
  const [loading, setLoading] = useState(true)

  // ✅ Fetch FAQs from API
  useEffect(() => {
    const fetchFaqs = async () => {
      setLoading(true)
      try {
        const res = await customerAuthService.getFaqs();
        if (res.status) {
          setFaqs(res?.data?.data || [])
        }
      } catch (error) {
      } finally {
        setLoading(false)
      }
    }

    fetchFaqs()
  }, [])

  // Filtering Logic
  useEffect(() => {
    let filtered = faqs.filter((faq) => faq.isActive)

    if (searchTerm) {
      filtered = filtered.filter(
        (faq) =>
          faq.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          faq.answer?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedCategory !== "all") {
      filtered = filtered.filter((faq) => faq.category === selectedCategory)
    }

    setFilteredFaqs(filtered)
  }, [faqs, searchTerm, selectedCategory])

  const categories = Array.from(new Set(faqs.filter((faq) => faq.isActive).map((faq) => faq.category)))

  return (
    <div className="min-h-screen bg-gray-50">
      <Header/>

      {/* Hero Section */}
      <div className="hbn-gradient mt-[5%]">
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Find answers to common questions about home buying and our services
          </p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="container mx-auto px-4 py-8">
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search FAQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 placeholder:text-black/70"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Results */}
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">
              {filteredFaqs.length} {filteredFaqs.length === 1 ? "Question" : "Questions"}
              {selectedCategory !== "all" && (
                <Badge variant="secondary" className="ml-3 bg-purple-100 text-purple-800">
                  {selectedCategory}
                </Badge>
              )}
            </h2>
          </div>

          {loading ? (
            <Card>
              <CardContent className="pt-12 pb-12 text-center">
                <p className="text-lg text-gray-500">Loading FAQs...</p>
              </CardContent>
            </Card>
          ) : filteredFaqs.length === 0 ? (
            <Card>
              <CardContent className="pt-12 pb-12 text-center">
                <p className="text-muted-foreground text-lg">No FAQs found matching your search criteria.</p>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFaqs.map((faq) => (
                <Card key={faq._id}>
                  <AccordionItem value={faq._id} className="border-0">
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-gray-50">
                      <div className="flex items-start justify-between w-full pr-4">
                        <span className="font-semibold text-gray-900">{faq.question}</span>
                        <Badge variant="outline" className="ml-4 shrink-0">
                          {faq.category}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-gray-700 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </Card>
              ))}
            </Accordion>
          )}
        </div>

        {/* Contact Section */}
        <div className="max-w-4xl mx-auto mt-16">
          <Card className="hbn-gradient text-white">
            <CardContent className="pt-8 pb-8 text-center">
              <h3 className="text-2xl font-bold mb-2">Still have questions?</h3>
              <p className="text-white/90 mb-6">
                {"Can't find what you're looking for? Our support team is here to help."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:hello@hbn-tech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors"
                >
                  Email Support
                </a>
                <a
                  href="tel:+1-800-HBN-HELP"
                  className="bg-white/10 text-black px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/20"
                >
                  Call Us: 0406 364 893
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
